/**
 * Marketing utilities: query parsing, CTA URL whitelist, FB Pixel & GTM
 */

import { CTA_DOMAIN_WHITELIST, getGtmId } from '../config';

// --- Debug helpers ---
export function isDebugEnabled(): boolean {
  try {
    const params = new URLSearchParams(window.location.search);
    const dbg = params.get('debug') || params.get('log');
    if (dbg && /^(1|true|yes|on|debug)$/i.test(dbg)) return true;
    const ls = window.localStorage?.getItem('debug_marketing');
    if (ls && /^(1|true|yes|on|debug)$/i.test(ls)) return true;
  } catch {
    // ignore
  }
  return false;
}

export function debugLog(...args: unknown[]): void {
  if (isDebugEnabled()) {
    console.log('[marketing]', ...args);
  }
}

// --- Query helpers ---
export function getQueryParam(name: string): string | null {
  if (typeof window === 'undefined') return null;
  const params = new URLSearchParams(window.location.search);
  const val = params.get(name);
  return val && val.trim().length > 0 ? val.trim() : null;
}

export function getPixelIdsFromQuery(): string[] {
  const raw = getQueryParam('pixel_id');
  if (!raw) return [];
  const ids = raw
    .split(',')
    .map((s) => s.trim())
    .filter((s) => /\d{5,}/.test(s));
  debugLog('pixel_id parsed:', ids);
  return ids;
}

export function getCtaUrlFromQuery(): string | null {
  const raw = getQueryParam('cta_url');
  if (!raw) return null;
  try {
    // Validate basic URL structure early
    // new URL will throw if invalid
    const u = new URL(raw);
    if (!u.protocol.startsWith('http')) return null;
    const url = u.toString();
    debugLog('cta_url parsed:', url);
    return url;
  } catch {
    return null;
  }
}

// --- CTA whitelist ---
export function isDomainAllowed(urlString: string, whitelist = CTA_DOMAIN_WHITELIST): boolean {
  try {
    const { hostname } = new URL(urlString);
    // Support wildcard by suffix match and exact match
    const allowed = whitelist.some((pattern) => {
      const p = pattern.toLowerCase();
      const h = hostname.toLowerCase();
      if (p === h) return true; // exact
      // allow subdomain: *.domain.tld
      return h.endsWith(`.${p}`);
    });
    debugLog('isDomainAllowed:', { url: urlString, hostname, allowed, whitelist });
    return allowed;
  } catch {
    return false;
  }
}

export function getEffectiveCtaUrl(candidateUrl: string | undefined | null, fallbackUrl?: string | null): string | null {
  // Prefer candidate if allowed; else try fallback; else null
  const candidateAllowed = !!candidateUrl && isDomainAllowed(candidateUrl);
  const fallbackAllowed = !!fallbackUrl && isDomainAllowed(fallbackUrl);
  const chosen = candidateAllowed ? candidateUrl : fallbackAllowed ? fallbackUrl! : null;
  debugLog('getEffectiveCtaUrl:', { candidateUrl, candidateAllowed, fallbackUrl, fallbackAllowed, chosen });
  return chosen;
}

// --- Facebook Pixel ---
declare global {
  interface Window {
    fbq?: (...args: unknown[]) => void;
    _fbq?: unknown;
    dataLayer?: unknown[];
    __fbPixelIds?: Set<string> | string[]; // track initialized pixels (Set in modern browsers)
    __fbTrackedPageView?: Set<string> | string[]; // pixels that already fired PageView
    __fbTrackedViewContent?: Set<string> | string[]; // pixels that already fired ViewContent
    _gtmLoaded?: boolean;
  }
}

export function initFacebookPixels(pixelIds: string[], withPageView: boolean = true): void {
  if (typeof window === 'undefined' || pixelIds.length === 0) return;
  debugLog('initFacebookPixels called with:', pixelIds, 'withPageView=', withPageView);

  // Inject fbq if not present - using Facebook's official bootstrap pattern
  if (!window.fbq) {
    type FbqStub = ((...args: unknown[]) => void) & {
      callMethod?: (...args: unknown[]) => void;
      queue?: unknown[][];
      push?: FbqStub;
      loaded?: boolean;
      version?: string;
    };

    // Official Facebook Pixel stub function
    const n = (function (...args: unknown[]) {
      if (n.callMethod) {
        n.callMethod(...args);
      } else {
        n.queue?.push(args);
      }
    }) as FbqStub;
    if (!window._fbq) {
      window._fbq = n;
    }
    n.push = n;
    n.loaded = true;
    n.version = '2.0';
    n.queue = [];
    window.fbq = n;

    const script = document.createElement('script');
    script.async = true;
    script.src = 'https://connect.facebook.net/en_US/fbevents.js';
    script.onload = () => {
      console.log('[marketing] fbevents.js loaded successfully');
      debugLog('fbevents.js loaded successfully');
    };
    script.onerror = () => {
      console.error('[marketing] Failed to load fbevents.js - check network or ad blockers');
      debugLog('Failed to load fbevents.js');
    };
    document.head.appendChild(script);
    console.log('[marketing] fbq bootstrap injected fbevents.js');
    debugLog('fbq bootstrap injected fbevents.js');
  }

  try {
    // Maintain a set of initialized IDs to avoid duplicates
    if (!window.__fbPixelIds) {
      try {
        window.__fbPixelIds = new Set<string>();
      } catch {
        window.__fbPixelIds = []; // fallback
      }
    }

    const addInitialized = (id: string) => {
      if (window.__fbPixelIds instanceof Set) {
        window.__fbPixelIds.add(id);
      } else if (Array.isArray(window.__fbPixelIds)) {
        if (!window.__fbPixelIds.includes(id)) window.__fbPixelIds.push(id);
      }
    };

    const isInitialized = (id: string) => {
      return window.__fbPixelIds instanceof Set
        ? window.__fbPixelIds.has(id)
        : Array.isArray(window.__fbPixelIds) && window.__fbPixelIds.includes(id);
    };

    // Maintain sets for per-pixel one-time page events
    const ensureSet = (key: '__fbTrackedPageView' | '__fbTrackedViewContent') => {
      if (!window[key]) {
        try {
          window[key] = new Set<string>();
        } catch {
          window[key] = [];
        }
      }
    };

    const hasTracked = (key: '__fbTrackedPageView' | '__fbTrackedViewContent', id: string): boolean => {
      const col = window[key];
      return col instanceof Set ? col.has(id) : Array.isArray(col) && col.includes(id);
    };

    const markTracked = (key: '__fbTrackedPageView' | '__fbTrackedViewContent', id: string) => {
      const col = window[key];
      if (col instanceof Set) col.add(id);
      else if (Array.isArray(col) && !col.includes(id)) col.push(id);
    };

    pixelIds.forEach((id) => {
      if (!isInitialized(id)) {
        debugLog('fbq init:', id);
        window.fbq && window.fbq('init', id);
        addInitialized(id);
      }
    });

    if (withPageView && window.fbq) {
      // Ensure tracking collections exist
      ensureSet('__fbTrackedPageView');
      ensureSet('__fbTrackedViewContent');

      // Fire both PageView and ViewContent exactly once per pixel ID
      pixelIds.forEach((id) => {
        if (!hasTracked('__fbTrackedPageView', id)) {
          debugLog('fbq trackSingle PageView for pixel:', id);
          window.fbq && window.fbq('trackSingle', id, 'PageView');
          markTracked('__fbTrackedPageView', id);
        }
        if (!hasTracked('__fbTrackedViewContent', id)) {
          debugLog('fbq trackSingle ViewContent for pixel:', id);
          window.fbq && window.fbq('trackSingle', id, 'ViewContent');
          markTracked('__fbTrackedViewContent', id);
        }
      });
    }
  } catch (e) {
    console.warn('FB Pixel init failed:', e);
  }
}

export function trackCtaClick(): void {
  if (typeof window === 'undefined') return;
  console.log('[marketing] trackCtaClick invoked');
  debugLog('trackCtaClick invoked');

  try {
    // Ensure FB is initialized if possible (handles fast clicks before init)
    if (!window.fbq) {
      const ids = getPixelIdsFromQuery();
      if (ids.length > 0) {
        console.log('[marketing] fbq missing at click; initializing with ids:', ids);
        debugLog('fbq missing at click; initializing with ids:', ids);
        initFacebookPixels(ids, false);
      } else {
        console.log('[marketing] fbq not available and no pixel_id in query');
      }
    }

    // Fire pixel events if fbq is available
    if (window.fbq) {
      console.log('[marketing] fbq available, firing AddToCart, Contact, and Purchase events');
      debugLog('fbq available, firing events');

      // Fire AddToCart event
      console.log('[marketing] Calling fbq("track", "AddToCart", ...)');
      window.fbq('track', 'AddToCart', {
        content_name: 'cta_click',
        value: 5.0,
        currency: 'USD',
      });

      // Fire Contact event
      console.log('[marketing] Calling fbq("track", "Contact", ...)');
      window.fbq('track', 'Contact', {
        content_name: 'cta_click',
      });

      // Fire Purchase event
      console.log('[marketing] Calling fbq("track", "Purchase", ...)');
      window.fbq('track', 'Purchase', {
        value: 5.0,
        currency: 'USD',
      });

      console.log('[marketing] FB pixel events fired successfully');
    } else {
      console.warn('[marketing] fbq not available for tracking - did you add ?pixel_id=YOUR_ID to the URL?');
      debugLog('fbq not available for tracking');
    }
  } catch (e) {
    console.error('[marketing] FB Pixel track failed:', e);
  }

  // Also emit a GTM-friendly custom event
  try {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ event: 'cta_click' });
    console.log('[marketing] dataLayer push event=cta_click');
    debugLog('dataLayer push event=cta_click');
  } catch (e) {
    console.error('[marketing] GTM dataLayer push failed:', e);
  }
}

// --- Google Tag Manager ---
export function initGTM(gtmId?: string): void {
  if (typeof window === 'undefined') return;
  const id = gtmId || getGtmId();
  if (!id) return;

  // Idempotent init
  if (window._gtmLoaded) return;
  window._gtmLoaded = true;

  // Setup dataLayer
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({ 'gtm.start': new Date().getTime(), event: 'gtm.js' });

  const gtmScript = document.createElement('script');
  gtmScript.async = true;
  gtmScript.src = `https://www.googletagmanager.com/gtm.js?id=${encodeURIComponent(id)}`;
  document.head.appendChild(gtmScript);
  debugLog('GTM injected with ID:', id);
}
